import { Phone, CalendarCheck, Hotel, EyeOff, Clock, FileText, CheckCircle2, TrendingDown, ArrowRight, Star, Quote, ChevronDown, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, MaskedLine, SectionTag } from "@/components/Reveal";
import CtaSection from "@/components/CtaSection";
import Seo from "@/components/Seo";
import GuaranteeBadge from "@/components/GuaranteeBadge";
import { CALENDLY_URL, SITE_URL } from "@/data/site";

const DISCRETION = [
    { icon: EyeOff, title: "Véhicule blanc banalisé", text: "Notre véhicule d'intervention est un utilitaire blanc sans aucun marquage visible. Ni vos clients, ni vos voisins ne savent pourquoi nous sommes là. Votre image reste intacte." },
    { icon: Clock, title: "De 8h à 22h, 7j/7", text: "Nous intervenons tôt le matin avant les départs, en journée entre deux séjours, ou en soirée après les arrivées. Vous choisissez le créneau qui ne perturbe personne." },
    { icon: FileText, title: "Rapport d'intervention écrit", text: "Après chaque passage, vous recevez un compte rendu écrit : une preuve de traitement à opposer en cas d'avis négatif ou de litige avec un client." },
];

const INCLUS = [
    "Recherche et diagnostic complet de la chambre (literie, sommier, tête de lit, plinthes, prises, meubles)",
    "Traitement vapeur sèche 180°C : adultes, larves et œufs détruits instantanément par la chaleur",
    "Aucun produit chimique : aucun délai de réintégration, aucune odeur, aucune trace",
    "Chambre relouable dès la fin de l'intervention, le jour même",
    "Rapport d'intervention écrit pour votre dossier qualité",
    "Conseils de prévention à transmettre à votre personnel d'étage pour détecter tôt",
];

const ETAPES = [
    { num: "1", title: "Vous appelez ou réservez", text: "Au 02 55 99 22 43 ou en ligne en 2 minutes. Décrivez la situation, on s'occupe du reste." },
    { num: "2", title: "Intervention discrète sous 2h", text: "Véhicule blanc banalisé, créneau de 8h à 22h, 7j/7. Personne ne remarque notre passage." },
    { num: "3", title: "Chambre relouée le jour même", text: "La vapeur ne laisse aucun résidu : la chambre est remise en location immédiatement." },
];

const FAQ = [
    { q: "La chambre peut-elle vraiment être relouée le jour même ?", a: "Oui. La vapeur sèche 180°C est 100 % naturelle : aucun produit chimique, aucune odeur, aucun résidu. Dès la fin de notre intervention, la chambre peut être remise en location. La plupart de nos passages en hôtel ont lieu entre le départ d'un client et l'arrivée du suivant." },
    { q: "Votre véhicule est-il vraiment discret ?", a: "Absolument. Nous intervenons avec un utilitaire blanc banalisé, sans aucun marquage visible. Ni vos clients, ni vos voisins ne connaissent la raison de notre passage. Nous pouvons aussi intervenir tôt le matin ou tard le soir, de 8h à 22h, 7j/7." },
    { q: "Combien coûte un traitement de punaises de lit en hôtel ?", a: "Le traitement vapeur d'une chambre démarre à 190 € HT. La recherche (49 à 59 € HT) est déduite si le traitement est réalisé le jour même. Pour plusieurs chambres ou un contrat annuel de protection, nous établissons un devis sur mesure." },
    { q: "Intervenez-vous en urgence dans les hôtels ?", a: "Oui, 7j/7 de 8h à 22h, généralement sous 2h dans un rayon de 80 km autour du Mont-Dol : Saint-Malo, Dinard, Cancale, Dol-de-Bretagne, Dinan, Rennes et plus de 150 communes." },
    { q: "Fournissez-vous une preuve d'intervention ?", a: "Oui. Après chaque passage, nous remettons un rapport d'intervention écrit. Il vous sert de preuve de traitement pour votre dossier qualité et pour répondre sereinement à un éventuel avis client." },
];

const TEMOIGNAGES = [
    { ville: "Saint-Malo", role: "Direction d'un hôtel", text: "Chambre signalée un matin à 9h, intervention à 11h en toute discrétion, chambre relouée le soir même. Aucun client ne s'est aperçu de rien, et le rapport écrit nous a servi pour répondre à l'avis laissé en ligne. C'est exactement le service dont un hôtelier a besoin." },
    { ville: "Dinard", role: "Gérant d'un hôtel", text: "Un client a signalé des piqûres un dimanche soir à 21h. Le technicien était sur place le lundi à 8h, avec un véhicule banalisé garé devant l'établissement. Aucune annulation, aucune mauvaise publicité." },
    { ville: "Cancale", role: "Responsable d'une résidence de tourisme", text: "Trois chambres traitées d'un coup entre le départ des clients et les arrivées suivantes. Efficace, discret, et le rapport écrit nous rassure vis-à-vis de notre assurance." },
    { ville: "Dol-de-Bretagne", role: "Directrice d'un hôtel", text: "En pleine saison, impossible de fermer une chambre deux nuits de suite. Avec la vapeur, la chambre était relouée le soir même. Un tarif très correct pour la tranquillité apportée." },
];

const VILLES_HOTELS = [
    { label: "Saint-Malo", to: "/punaises-de-lit/saint-malo" },
    { label: "Dinard", to: "/punaises-de-lit/dinard" },
    { label: "Cancale", to: "/punaises-de-lit/cancale" },
    { label: "Dol-de-Bretagne", to: "/punaises-de-lit/dol-de-bretagne" },
    { label: "Dinan", to: "/punaises-de-lit/dinan" },
    { label: "Combourg", to: "/punaises-de-lit/combourg" },
    { label: "Pontorson & Mont-Saint-Michel", to: "/punaises-de-lit/pontorson" },
    { label: "Rennes", to: "/punaises-de-lit/rennes" },
    { label: "Fougères", to: "/punaises-de-lit/fougeres" },
    { label: "Vitré", to: "/punaises-de-lit/vitre" },
];

const Hotels = () => (
    <div data-testid="hotels-page">
        <Seo
            title="Punaises de lit hôtel : traitement discret, chambre relouée le jour même | Eradikbreizh"
            description="Traitement punaises de lit pour hôtels à Saint-Malo, Dinard, Cancale, Dol-de-Bretagne et toute la Bretagne. Vapeur 180°C dès 190 € HT par chambre, véhicule banalisé, intervention discrète de 8h à 22h, 7j/7. Chambre relouable le jour même, rapport écrit fourni."
            jsonLd={{
                "@context": "https://schema.org",
                "@graph": [
                    {
                        "@type": "Service",
                        serviceType: "Traitement des punaises de lit pour hôtels et hébergements touristiques",
                        name: "Traitement punaises de lit hôtel",
                        provider: {
                            "@type": "LocalBusiness",
                            name: "Eradikbreizh",
                            telephone: "+33255992243",
                            url: SITE_URL,
                        },
                        areaServed: ["Saint-Malo", "Dinard", "Cancale", "Dol-de-Bretagne", "Dinan", "Rennes", "Bretagne"],
                        hoursAvailable: "Mo-Su 08:00-22:00",
                        offers: {
                            "@type": "Offer",
                            priceCurrency: "EUR",
                            price: "190",
                            description: "Traitement vapeur sèche 180°C d'une chambre d'hôtel, à partir de 190 € HT, chambre relouable le jour même.",
                        },
                    },
                    {
                        "@type": "FAQPage",
                        mainEntity: FAQ.map((f) => ({
                            "@type": "Question",
                            name: f.q,
                            acceptedAnswer: { "@type": "Answer", text: f.a },
                        })),
                    },
                ],
            }}
        />

        {/* HERO */}
        <section className="relative overflow-hidden bg-gradient-to-b from-mist to-white pb-20 pt-40 md:pt-48">
            <div className="absolute -right-40 top-0 h-[30rem] w-[30rem] rounded-full bg-brand/10 blur-[160px]" aria-hidden="true" />
            <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
                <MaskedLine delay={0.1}>
                    <span className="inline-flex items-center gap-3 rounded-full border border-black/10 bg-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-slate-600 shadow-sm">
                        <Hotel className="h-4 w-4 text-brand" />
                        Hôtels &amp; hébergements touristiques
                    </span>
                </MaskedLine>
                <MaskedLine delay={0.25}>
                    <h1 className="mt-8 max-w-4xl font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-6xl">
                        Une chambre infestée ? <span className="text-brand">Relouée dès ce soir.</span>
                    </h1>
                </MaskedLine>
                <MaskedLine delay={0.4}>
                    <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
                        Punaises de lit dans votre hôtel : nous intervenons en toute discrétion avec un véhicule blanc banalisé, de 8h à 22h, 7j/7. Grâce à la vapeur sèche 180°C, sans aucun produit chimique, votre chambre est relouable le jour même.
                    </p>
                </MaskedLine>
                <MaskedLine delay={0.55}>
                    <div className="mt-10 flex flex-wrap items-center gap-4">
                        <a
                            data-testid="hotels-phone-button"
                            href="tel:+33255992243"
                            className="inline-flex items-center gap-2 rounded-full bg-brand px-8 py-4 font-bold text-white transition-[background-color,transform] duration-300 hover:bg-brand-hover hover:scale-105 active:scale-95"
                        >
                            <Phone className="h-5 w-5" />
                            02 55 99 22 43
                        </a>
                        <a
                            data-testid="hotels-booking-button"
                            href={CALENDLY_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 rounded-full border border-black/15 px-8 py-4 font-bold text-night transition-[border-color,background-color] duration-300 hover:border-brand hover:bg-black/5"
                        >
                            <CalendarCheck className="h-5 w-5 text-brand" />
                            Réserver une intervention discrète
                        </a>
                    </div>
                </MaskedLine>
                <MaskedLine delay={0.7}>
                    <div className="mt-8 flex flex-wrap gap-3 text-xs font-bold uppercase tracking-[0.15em]">
                        <span className="rounded-full border border-black/10 bg-white px-4 py-2 text-slate-600 shadow-sm">Véhicule banalisé</span>
                        <span className="rounded-full border border-brand/30 bg-brand/5 px-4 py-2 text-brand">Dès 190 € HT / chambre</span>
                        <span className="rounded-full border border-black/10 bg-white px-4 py-2 text-slate-600 shadow-sm">8h : 22h, 7j/7</span>
                    </div>
                </MaskedLine>
            </div>
        </section>

        {/* COUT REEL */}
        <section className="bg-white py-20 md:py-24">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <Reveal><SectionTag>Le calcul est simple</SectionTag></Reveal>
                <Reveal delay={0.1}>
                    <h2 className="mt-6 max-w-3xl font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                        Une chambre fermée coûte plus cher <span className="text-brand">qu'un traitement</span>
                    </h2>
                </Reveal>
                <div className="mt-14 grid gap-6 lg:grid-cols-2">
                    <Reveal delay={0.1} className="h-full">
                        <div data-testid="hotels-comparison-closed" className="h-full rounded-3xl border border-red-200 bg-red-50/60 p-8 sm:p-10">
                            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-100 text-red-500">
                                <TrendingDown className="h-6 w-6" />
                            </div>
                            <h3 className="mt-5 font-display text-lg font-black uppercase tracking-tight text-night">Chambre bloquée 3 nuits</h3>
                            <ul className="mt-4 space-y-3 text-sm leading-relaxed text-slate-600 sm:text-base">
                                <li>Manque à gagner : 270 à 450 € de nuitées perdues (chambre à 90-150 € / nuit)</li>
                                <li>Un avis Google 1 étoile qui reste en ligne pour des années</li>
                                <li>Remboursements, relogements et gestion de crise en réception</li>
                                <li>Risque de propagation aux chambres voisines chaque jour d'attente</li>
                            </ul>
                        </div>
                    </Reveal>
                    <Reveal delay={0.2} className="h-full">
                        <div data-testid="hotels-comparison-treated" className="h-full rounded-3xl border border-brand/30 bg-brand/5 p-8 sm:p-10">
                            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand text-white">
                                <Hotel className="h-6 w-6" />
                            </div>
                            <h3 className="mt-5 font-display text-lg font-black uppercase tracking-tight text-night">Traitement vapeur Eradikbreizh</h3>
                            <ul className="mt-4 space-y-3 text-sm leading-relaxed text-slate-600 sm:text-base">
                                <li><strong className="text-brand">Dès 190 € HT par chambre</strong>, recherche déduite si traitement le jour même</li>
                                <li>Chambre <strong className="text-brand">relouable le jour même</strong> : vapeur 180°C, zéro produit chimique, zéro délai</li>
                                <li>Intervention invisible : véhicule blanc banalisé, créneau 8h-22h</li>
                                <li>Rapport écrit fourni pour répondre sereinement à tout avis client</li>
                            </ul>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>

        {/* DISCRETION */}
        <section className="bg-mist py-20 md:py-24">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <Reveal><SectionTag>Discrétion absolue</SectionTag></Reveal>
                <Reveal delay={0.1}>
                    <h2 className="mt-6 max-w-3xl font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                        Personne ne saura <span className="text-brand">que nous sommes passés</span>
                    </h2>
                </Reveal>
                <div className="mt-14 grid items-start gap-10 lg:grid-cols-2">
                    <Reveal className="h-full">
                        <figure>
                            <img
                                data-testid="hotels-van-photo"
                                src="/assets/camion-banalise.jpg"
                                alt="Véhicule d'intervention Eradikbreizh blanc et banalisé, sans aucun marquage visible"
                                className="w-full rounded-3xl border border-black/10 object-cover shadow-lg"
                                loading="lazy"
                            />
                            <figcaption className="mt-3 text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                Notre véhicule d'intervention : blanc, banalisé, sans aucun marquage
                            </figcaption>
                        </figure>
                    </Reveal>
                    <div className="grid gap-6">
                        {DISCRETION.map((d, i) => (
                            <Reveal key={d.title} delay={0.08 * i} className="h-full">
                                <div data-testid={`hotels-discretion-${i}`} className="h-full rounded-3xl border border-black/10 bg-white p-8 shadow-sm transition-transform duration-300 hover:-translate-y-1">
                                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand/10 text-brand">
                                        <d.icon className="h-6 w-6" />
                                    </div>
                                    <h3 className="mt-5 font-display text-lg font-black uppercase tracking-tight">{d.title}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-slate-600">{d.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </div>
        </section>

        {/* PRESTATION INCLUSE */}
        <section className="bg-white py-20 md:py-24">
            <div className="mx-auto grid max-w-7xl items-start gap-12 px-5 sm:px-8 lg:grid-cols-2">
                <div>
                    <Reveal><SectionTag>La prestation vapeur</SectionTag></Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-6 font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                            Tout est inclus, <span className="text-brand">sans surprise</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-6 text-sm leading-relaxed text-slate-600 sm:text-base">
                            La vapeur sèche 180°C est la méthode la plus adaptée à l'hôtellerie : 100 % naturelle, elle détruit punaises, larves et œufs en un seul passage, sans fermer la chambre ni déplacer vos clients.
                        </p>
                    </Reveal>
                    <Reveal delay={0.2} className="mt-8">
                        <GuaranteeBadge testid="hotels-guarantee-badge" />
                    </Reveal>
                </div>
                <div className="space-y-4">
                    {INCLUS.map((item, i) => (
                        <Reveal key={item} delay={0.06 * i}>
                            <div data-testid={`hotels-inclus-${i}`} className="flex items-start gap-4 rounded-2xl border border-black/10 bg-mist px-6 py-4">
                                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
                                <p className="text-sm leading-relaxed text-slate-700">{item}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>

        {/* TARIF HOTEL */}
        <section className="bg-brand py-20 text-white md:py-24">
            <div className="mx-auto max-w-7xl px-5 text-center sm:px-8">
                <Reveal>
                    <p className="text-xs font-bold uppercase tracking-[0.3em] text-white/70">Tarif professionnels de l'hôtellerie</p>
                </Reveal>
                <Reveal delay={0.1}>
                    <p data-testid="hotels-price" className="mt-6 font-display text-5xl font-black tracking-tight sm:text-7xl">
                        Dès 190 € HT <span className="text-2xl font-bold text-white/80 sm:text-3xl">/ chambre</span>
                    </p>
                </Reveal>
                <Reveal delay={0.15}>
                    <p className="mx-auto mt-6 max-w-2xl text-sm leading-relaxed text-white/85 sm:text-base">
                        Prestation vapeur complète, chambre relouable le jour même. Recherche à 49-59 € déduite si le traitement est réalisé immédiatement. Plusieurs chambres ou un étage complet : tarif sur devis, contrats annuels de protection possibles. Majoration de 20 % les samedis, dimanches et jours fériés.
                    </p>
                </Reveal>
                <Reveal delay={0.2}>
                    <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                        <a
                            data-testid="hotels-price-call-button"
                            href="tel:+33255992243"
                            className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 font-bold text-brand transition-[background-color,transform] duration-300 hover:bg-slate-100 hover:scale-105 active:scale-95"
                        >
                            <Phone className="h-5 w-5" />
                            Appeler maintenant
                        </a>
                        <a
                            data-testid="hotels-price-booking-button"
                            href={CALENDLY_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 rounded-full border-2 border-white/40 px-8 py-4 font-bold text-white transition-[background-color,border-color] duration-300 hover:border-white hover:bg-white/10"
                        >
                            <CalendarCheck className="h-5 w-5" />
                            Réserver en ligne en 2 min
                        </a>
                        <Link
                            data-testid="hotels-tarifs-link"
                            to="/tarifs"
                            className="inline-flex items-center gap-2 text-sm font-bold text-white/85 underline-offset-4 transition-colors hover:text-white hover:underline"
                        >
                            Voir toute la grille tarifaire
                            <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </Reveal>
            </div>
        </section>

        {/* TEMOIGNAGES HOTELIERS */}
        <section className="bg-mist py-20 md:py-24">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <Reveal><SectionTag>Ils nous ont fait confiance</SectionTag></Reveal>
                <Reveal delay={0.1}>
                    <h2 className="mt-6 max-w-3xl font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                        Des hôteliers <span className="text-brand">de la côte</span> témoignent
                    </h2>
                </Reveal>
                <div className="mt-14 grid gap-6 md:grid-cols-2">
                    {TEMOIGNAGES.map((t, i) => (
                        <Reveal key={t.ville} delay={0.08 * i} className="h-full">
                            <figure data-testid={`hotels-testimonial-${i}`} className="flex h-full flex-col rounded-3xl border border-black/10 bg-white p-8 shadow-sm sm:p-10">
                                <Quote className="h-8 w-8 text-brand/30" />
                                <div className="mt-3 flex gap-1" aria-label="5 étoiles sur 5">
                                    {[0, 1, 2, 3, 4].map((s) => (
                                        <Star key={s} className="h-4 w-4 fill-amber-400 text-amber-400" />
                                    ))}
                                </div>
                                <blockquote className="mt-5 flex-1 text-sm leading-relaxed text-night sm:text-base">
                                    « {t.text} »
                                </blockquote>
                                <figcaption className="mt-6 flex items-center gap-4 border-t border-black/10 pt-6">
                                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand">
                                        <Hotel className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <p className="font-bold text-night">{t.role}, {t.ville}</p>
                                        <p className="text-xs text-slate-500">Témoignage anonymisé à la demande de l'établissement</p>
                                    </div>
                                </figcaption>
                            </figure>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>

        {/* PROCESS */}
        <section className="bg-white py-20 md:py-24">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <Reveal><SectionTag>Comment ça se passe</SectionTag></Reveal>
                <Reveal delay={0.1}>
                    <h2 className="mt-6 max-w-3xl font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                        De l'alerte à la relocation <span className="text-brand">en une journée</span>
                    </h2>
                </Reveal>
                <div className="mt-14 grid gap-6 md:grid-cols-3">
                    {ETAPES.map((e, i) => (
                        <Reveal key={e.num} delay={0.08 * i} className="h-full">
                            <div data-testid={`hotels-step-${i}`} className="h-full rounded-3xl border border-black/10 bg-mist p-8">
                                <span className="font-display text-5xl font-black text-brand/20">{e.num}</span>
                                <h3 className="mt-4 font-display text-lg font-black uppercase tracking-tight">{e.title}</h3>
                                <p className="mt-3 text-sm leading-relaxed text-slate-600">{e.text}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>

        {/* FAQ HOTELIERS */}
        <section className="bg-white py-20 md:py-24">
            <div className="mx-auto max-w-4xl px-5 sm:px-8">
                <Reveal><SectionTag>Questions fréquentes des hôteliers</SectionTag></Reveal>
                <Reveal delay={0.1}>
                    <h2 className="mt-6 font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                        Punaises de lit en hôtel : <span className="text-brand">vos questions, nos réponses</span>
                    </h2>
                </Reveal>
                <div className="mt-12 space-y-4">
                    {FAQ.map((f, i) => (
                        <Reveal key={f.q} delay={0.05 * i}>
                            <details data-testid={`hotels-faq-${i}`} className="group rounded-2xl border border-black/10 bg-mist px-6 py-5">
                                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-bold text-night [&::-webkit-details-marker]:hidden">
                                    {f.q}
                                    <ChevronDown className="h-5 w-5 shrink-0 text-brand transition-transform duration-300 group-open:rotate-180" />
                                </summary>
                                <p className="mt-4 text-sm leading-relaxed text-slate-600">{f.a}</p>
                            </details>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>

        {/* ZONE HOTELIERE */}
        <section className="bg-mist py-20 md:py-24">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <Reveal>
                    <SectionTag>Zone d'intervention hôtelière</SectionTag>
                </Reveal>
                <Reveal delay={0.1}>
                    <h2 className="mt-6 max-w-3xl font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                        Traitement punaises de lit pour hôtels <span className="text-brand">dans toute la Bretagne</span>
                    </h2>
                </Reveal>
                <Reveal delay={0.15}>
                    <p className="mt-6 max-w-3xl text-sm leading-relaxed text-slate-600 sm:text-base">
                        Hôtels, gîtes, chambres d'hôtes, campings, résidences de tourisme et conciergeries : nous intervenons sous 2h dans un rayon de 80 km autour du Mont-Dol, 7j/7 de 8h à 22h. Baie de Saint-Malo, Côte d'Émeraude, Dinard, Cancale, vallée de la Rance, Dinan, Dol-de-Bretagne, Combourg, baie du Mont-Saint-Michel, Rennes, Fougères, Vitré et plus de 150 communes.
                    </p>
                </Reveal>
                <Reveal delay={0.2}>
                    <div className="mt-8 flex flex-wrap gap-3">
                        {VILLES_HOTELS.map((v) => (
                            <Link
                                key={v.to}
                                data-testid={`hotels-ville-${v.to.split("/").pop()}`}
                                to={v.to}
                                className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-5 py-2.5 text-sm font-semibold text-night shadow-sm transition-[border-color,color] duration-300 hover:border-brand hover:text-brand"
                            >
                                <MapPin className="h-4 w-4 text-brand" />
                                {v.label}
                            </Link>
                        ))}
                        <Link
                            data-testid="hotels-zone-link"
                            to="/zone-intervention"
                            className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white transition-[background-color,transform] duration-300 hover:bg-brand-hover hover:scale-[1.02]"
                        >
                            Toute la zone d'intervention
                            <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </Reveal>
            </div>
        </section>

        <CtaSection
            title="Protégez votre établissement dès aujourd'hui"
            subtitle="Un appel suffit : intervention discrète sous 2h, de 8h à 22h, 7j/7, partout dans un rayon de 80 km autour du Mont-Dol."
        />
    </div>
);

export default Hotels;
